/** @jsxImportSource @emotion/react */
import { Interpolation, SerializedStyles, Theme, css } from '@emotion/react';
import { Virtuoso, VirtuosoHandle } from 'react-virtuoso';
import { useEffect, useCallback, useRef, useState, useMemo, Fragment, ReactNode } from 'react';
import { Box, ButtonBase, Popover, TextField } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import usePrevious from '../../hooks/usePrevious';
import { PaginatedQueryRequest, QueryRequest } from '../../typings/request';
import CaretIcon from '../../icons/Caret';
import { QSButton, QSTypography } from '../../components/atoms';
import Loader from '../../components/molecules/loader';
import EmptyDataMessage from '../empty-data-message';
import CloseIcon from '@mui/icons-material/Close';
import { QSCheckbox } from '../../components/atoms/form';
import ArrowExpandIcon from '../../icons/icons-revamp/arrow_expand';

const DropdownVirtualList = <
  T extends { id: V; [key: string]: any },
  K extends { hasMore: boolean },
  V = string,
  Multiple = boolean
>({
  disabled,
  popoverLabel,
  popoverValue,
  searchTerm,
  setSearchTerm,
  placeholder,
  optionsEmptyMessage,
  name,
  value,
  onChange,
  closeOnSelect,
  multiple,
  allValuesId,
  disablePortal,
  fetchMore,
  requestData,
  refresh,
  containerStyles,
  popoverLabelStyles,
  placeholderStyles,
  buttonStyles,
  emptyDataMessageStyles,
  emptyDataContainerStyles,
  showCloseIcon,
  onCloseIconClick,
  onDropdownOpen,
  onDropdownClose,
  allowNoSelection = true,
  customHandleChange,
  caretIconCss,
  hoverBorderColor = '#2d2d2d',
  disableSearch = false,
  customButton,
  customButtonBottom,
  open,
  paperWidth,
  paperHeight,
  renderItemPrefix,
}: {
  disabled?: boolean;
  popoverLabel?: string;
  popoverValue?: string;
  searchTerm?: string;
  setSearchTerm?: (searchTerm: string) => void;
  placeholder?: string;
  optionsEmptyMessage?: string;
  name: (keyof T)[];
  value: Multiple extends true ? V[] : V;
  onChange: Multiple extends true ? (value: V[]) => void : (value: V) => void;
  closeOnSelect?: boolean;
  multiple?: Multiple;
  allValuesId?: V;
  disablePortal?: boolean;
  fetchMore?: () => Promise<void>;
  requestData?: PaginatedQueryRequest<T, K> | QueryRequest<T[]>;
  refresh?: () => Promise<T[] | void | undefined>;
  containerStyles?: SerializedStyles;
  popoverLabelStyles?: SerializedStyles;
  placeholderStyles?: SerializedStyles;
  buttonStyles?: SerializedStyles;
  emptyDataMessageStyles?: SerializedStyles;
  emptyDataContainerStyles?: SerializedStyles;
  showCloseIcon?: boolean;
  onCloseIconClick?: () => void;
  onDropdownOpen?: () => void;
  onDropdownClose?: () => void;
  allowNoSelection?: boolean;
  customHandleChange?: (item: T) => void;
  caretIconCss?: Interpolation<Theme>;
  hoverBorderColor?: string;
  disableSearch?: boolean;
  customButton?: ReactNode;
  customButtonBottom?: ReactNode;
  open?: boolean;
  paperWidth?: string;
  paperHeight?: string;
  renderItemPrefix?: (item: T) => ReactNode;
}) => {
  const anchorEl = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const [dropdownVisible, setDropdownVisible] = useState<boolean>(false);

  // Use external open prop if provided, otherwise use internal state
  const isDropdownOpen = open !== undefined ? open : dropdownVisible;

  const prevSearchTerm = usePrevious<string | undefined>(searchTerm);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const virtualListRef = useRef<VirtuosoHandle>(null);

  useEffect(() => {
    if (virtualListRef.current) {
      virtualListRef.current.scrollToIndex({
        index: 0,
        behavior: 'auto',
      });
    }
  }, [virtualListRef, searchTerm]);

  const filteredRequestData = useMemo(() => {
    let data = requestData?.data ?? [];

    if (disableSearch) {
      return data;
    }

    const term = searchTerm?.trim().toLowerCase() ?? '';

    if (term.length >= 1) {
      const isNumericSearchTerm = /^\d+$/.test(term);
      const numberRelatedKeys = name.filter((key) => /(phone|mobile|number|id|waba)/i.test(String(key)));
      const keysToSearch = isNumericSearchTerm && numberRelatedKeys.length > 0 ? numberRelatedKeys : name;

      data = data.filter((item) => {
        for (const key of keysToSearch) {
          const itemValue = item[key];
          if (itemValue !== null && itemValue !== undefined) {
            const valueType = typeof itemValue;
            if (valueType === 'string' || valueType === 'number' || valueType === 'boolean') {
              if (String(itemValue).toLowerCase().includes(term)) {
                return true;
              }
            }
          }
        }
        return false;
      });
    }

    return data;
  }, [requestData?.data, searchTerm, disableSearch, name]);

  const handleDropdownOpen = useCallback(() => {
    setDropdownVisible(true);
    onDropdownOpen?.();
  }, [setDropdownVisible, onDropdownOpen]);

  const handleDropdownClose = useCallback(() => {
    setDropdownVisible(false);
    onDropdownClose?.();
  }, [setDropdownVisible, onDropdownClose]);

  const getDisplayName = useCallback(
    (item: T) => {
      for (const index in name) {
        if (['string', 'number', 'boolean'].indexOf(typeof item[name[index]]) > -1) {
          return item[name[index]];
        }
      }
      return item.id as string;
    },
    [name]
  );

  const onEscapeKeyPress = useCallback(
    (event: KeyboardEvent) => {
      if (isDropdownOpen && event.key === 'Escape') {
        handleDropdownClose();
      }
    },
    [isDropdownOpen, handleDropdownClose]
  );

  useEffect(() => {
    if (!disableSearch && typeof prevSearchTerm === 'string' && prevSearchTerm !== searchTerm) {
      refresh?.();
    }
  }, [searchTerm, prevSearchTerm, refresh, disableSearch]);

  useEffect(() => {
    if (typeof isDropdownOpen === 'boolean') {
      if (isDropdownOpen && !disableSearch) {
        setTimeout(() => {
          if (typeof searchInputRef.current?.focus === 'function') {
            searchInputRef.current?.focus();
          }
        }, 200);
      } else if (!isDropdownOpen && !disableSearch) {
        setTimeout(() => {
          if (typeof searchInputRef.current?.value === 'string' && searchInputRef.current?.value?.length > 0) {
            searchInputRef.current.value = '';
            setSearchTerm?.('');
          }
        }, 200);
      }
    }
  }, [isDropdownOpen, setSearchTerm, disableSearch]);

  useEffect(() => {
    document.addEventListener('keydown', onEscapeKeyPress);
    return () => {
      document.removeEventListener('keydown', onEscapeKeyPress);
    };
  }, [onEscapeKeyPress]);

  const handleChange = useCallback(
    (item: T) => {
      if (multiple) {
        if ((value as V[]).indexOf(item.id) > -1) {
          (onChange as (value: V[]) => void)(
            (value as V[]).filter((id, _, value) => {
              if (typeof allValuesId === 'string' && allValuesId.length > 0 && value.includes(allValuesId)) {
                return id !== item.id && id !== allValuesId;
              }
              return id !== item.id;
            })
          );
        } else {
          if (
            typeof allValuesId === 'string' &&
            allValuesId.length > 0 &&
            (requestData?.data ?? []).findIndex(({ id }) => id === allValuesId) > -1
          ) {
            if (item.id === allValuesId) {
              (onChange as (value: V[]) => void)([allValuesId]);
              handleDropdownClose();
            } else {
              (onChange as (value: V[]) => void)(
                [...new Set([...((value as V[]) ?? []), item.id])].filter((id) => id !== allValuesId)
              );
            }
          } else {
            (onChange as (value: V[]) => void)([...new Set([...((value as V[]) ?? []), item.id])]);
          }
        }
      } else {
        if (item.id !== (value as V)) {
          (onChange as (value: V) => void)(item.id);
        }
      }
      if (closeOnSelect) {
        handleDropdownClose();
      }
    },
    [multiple, closeOnSelect, value, onChange, allValuesId, requestData?.data, handleDropdownClose]
  );

  const handleSelectItem = customHandleChange ? customHandleChange : handleChange;

  const rowRenderer = useCallback(
    (index: number, item: any) => {
      if (!item || !item.id) {
        return null;
      }

      const isItemDisabled =
        multiple && !allowNoSelection && (value as V[])?.length === 1 && (value as V[])?.[0] === item.id;

      return (
        <Fragment>
          <ButtonBase<'div'>
            css={css`
              height: auto;
              min-height: 36px;
              width: 100%;
              max-width: 100%;
              display: flex;
              justify-content: space-between;
              align-items: center;
              column-gap: 12px;
              background-color: ${(multiple ? ((value as V[]) ?? []).indexOf(item.id) > -1 : item.id === value)
                ? 'rgba(93, 183, 121, 0.10)'
                : '#FFFFFF'};
              padding: 8px 12px;
              box-shadow: none;
              outline: none;
              word-wrap: break-word;
              overflow-wrap: break-word;
              flex-wrap: wrap;

              &:hover,
              &:focus {
                box-shadow: none;
                outline: none;
                background-color: ${(multiple ? ((value as V[]) ?? []).indexOf(item.id) > -1 : item.id === value)
                  ? 'rgba(93, 183, 121, 0.10)'
                  : 'rgba(0, 0, 0, 0.04)'};
              }
            `}
            tabIndex={0}
            disabled={disabled || isItemDisabled}
            onClick={() => handleSelectItem(item)}
            component="div"
          >
            {multiple && (
              <QSCheckbox
                checked={(value as V[]).indexOf(item.id) > -1}
                onClick={!isItemDisabled ? () => handleSelectItem(item) : undefined}
                sx={{
                  padding: 0,
                  color: '#717171',
                }}
                disabled={isItemDisabled}
              />
            )}
            {renderItemPrefix?.(item)}
            <QSTypography
              css={css`
                font-size: 14px;
                font-weight: 400;
                line-height: 1.5;
                color: #2d2d2d;
                white-space: initial;
                word-wrap: break-word;
                word-break: break-all;
                overflow-wrap: break-word;
                flex: 1;
              `}
            >
              {getDisplayName(item) ?? ''}
            </QSTypography>
            {/* {multiple && (value as V[]).indexOf(item.id) > -1 && (
          <CheckIcon
            css={css`
              color: #5bab83;
              height: 16px;
              min-height: 16px;
              width: 16px;
              min-width: 16px;
            `}
          />
        )} */}
          </ButtonBase>
        </Fragment>
      );
    },
    [multiple, value, handleSelectItem, disabled, renderItemPrefix]
  );

  return (
    <Box
      css={css(
        `
          width: 100%;
          max-width: 250px;
          display: flex;
          flex-direction: column;
          justify-content: flex-start;
          align-items: flex-start;
          row-gap: 8px;

          @media (min-width: 0px) and (max-width: 599px) {
            max-width: calc(50% - 8px);
          }
        `,
        containerStyles
      )}
    >
      {typeof popoverLabel === 'string' && popoverLabel.length > 0 && (
        <QSTypography
          css={css(
            `
              max-width: 100%;
              color: ${disabled ? 'rgba(0, 0, 0, 0.38)' : '#2D2D2D'};
              font-size: 14px;
              font-weight: 400;
              text-overflow: ellipsis;
              white-space: nowrap;
              overflow: hidden;
              word-wrap: break-word;
              word-break: break-all;
            `,
            popoverLabelStyles
          )}
        >
          {popoverLabel}
        </QSTypography>
      )}
      <ButtonBase<'div'>
        ref={anchorEl}
        css={css(
          `
            height: 40px;
            width: 100%;
            display: flex;
            justify-content: flex-start;
            align-items: center;
            flex: 1 1 auto;
            padding: 8px 32px 8px 12px;
            color: inherit;
            border: 1px solid ${isDropdownOpen ? '#2d2d2d' : '#E0E0EA'};
            background-color: #FFFFFF;
            border-radius: 4px;
            position: relative;
            user-select: none;
            box-shadow: none;
            outline: none;
            cursor: pointer;

            &:hover {
              box-shadow: none;
              outline: none;
              border-color: ${hoverBorderColor};
            }

            &:focus {
              box-shadow: none;
              outline: none;
            }
          `,
          buttonStyles
        )}
        tabIndex={0}
        onClick={handleDropdownOpen}
        disabled={disabled}
        disableRipple={true}
        component="div"
      >
        <QSTypography
          css={css(
            `
              max-width: 100%;
              color: ${disabled ? 'rgba(0, 0, 0, 0.38)' : '#2D2D2D'};
              font-size: 14px;
              font-weight: 400;
              text-overflow: ellipsis;
              white-space: nowrap;
              overflow: hidden;
              word-wrap: break-word;
              word-break: break-all;
              width: ${showCloseIcon ? `calc(100% - 16px)` : `unset`};
              margin-right: 4px;
            `,
            placeholderStyles
          )}
        >
          {popoverValue ?? (value as string) ?? placeholder ?? 'Please select a value'}
        </QSTypography>
        {showCloseIcon && Array.isArray(value) && value.length > 0 ? (
          <CloseIcon
            css={css`
              width: 16px;
              min-width: 16px;
              height: 16px;
              min-height: 16px;
              display: inline-block;
              fill: currentColor;
              color: rgba(0, 0, 0, 0.54);
              font-size: 1.5rem;
              cursor: pointer;
              position: absolute;
              right: 27px;
              top: calc(50% - 0.35em);
              user-select: none;
              margin-right: 3px;
            `}
            onClick={(e) => {
              e.stopPropagation();
              onCloseIconClick?.();
            }}
          />
        ) : null}

        <ArrowExpandIcon
          color="#7a7a7a"
          size={28}
          css={[
            css`
              width: 28px;
              min-width: 28px;
              height: 28px;
              min-height: 28px;
              display: inline-block;
              // fill: currentColor;
              color: rgba(0, 0, 0, 0.54);
              font-size: 1.5rem;
              pointer-events: none;
              position: absolute;
              right: 7px;
              top: calc(50% - 0.55em);
              user-select: none;
              transform: rotateZ(${isDropdownOpen ? 180 : 0}deg);
              transition: fill 200ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
            `,
            caretIconCss,
          ]}
        />
      </ButtonBase>
      <Popover
        open={isDropdownOpen}
        anchorEl={anchorEl.current}
        disablePortal={disablePortal}
        onClose={handleDropdownClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        slotProps={{
          paper: {
            sx: {
              width: paperWidth ? paperWidth : 'max-content',
              display: anchorEl.current?.clientWidth ? 'block' : 'none',
              minWidth: anchorEl.current?.clientWidth ?? '0px',
            },
          },
        }}
      >
        {!disableSearch && (
          <TextField
            autoFocus={true}
            autoCapitalize="off"
            autoCorrect="off"
            autoComplete="new-password"
            spellCheck="false"
            fullWidth={true}
            multiline={false}
            placeholder={placeholder ?? 'Please select a value'}
            inputRef={searchInputRef}
            onKeyDownCapture={(event) => {
              if (event.key === 'Escape') {
                handleDropdownClose();
              }
            }}
            InputProps={{
              type: 'search',
              sx: {
                overflow: 'hidden',
                '.MuiInputBase-input': {
                  fontSize: '14px',
                  textOverflow: 'ellipsis',
                },
              },
            }}
            inputProps={{
              sx: {
                '&::placeholder': {
                  color: 'inherit',
                  opacity: '1',
                },
                '&::-webkit-search-cancel-button': {
                  display: 'none',
                },
              },
              autoFocus: true,
              autoComplete: 'new-password',
            }}
            sx={{
              height: '40px',
              lineHeight: '40px',
              width: '100%',
              borderBottom: '1px solid #E0E0EA',
              overflow: 'hidden',
              '.MuiInputBase-input': {
                height: 'calc(40px - 2 * 8px)',
                width: '100%',
                padding: '8px 12px',
                fontSize: '14px',
                fontWeight: '400',
                color: 'rgba(0, 0, 0, 0.6)',
              },
              '.MuiOutlinedInput-notchedOutline': {
                border: 'none',
              },
              '.MuiInputBase-root': {
                color: 'inherit',
                borderRadius: '8px',
                '&.Mui-focused': {
                  '.MuiOutlinedInput-notchedOutline': {
                    border: 'none',
                  },
                },
                ':hover': {
                  '.MuiOutlinedInput-notchedOutline': {
                    border: 'none',
                  },
                },
              },
            }}
            onChange={({ target: { value } }) => setSearchTerm?.(value)}
            onKeyDown={(event) => event.stopPropagation()}
          />
        )}
        {customButton ? customButton : null}
        <Virtuoso<T>
          style={{
            height:
              !requestData?.data || requestData?.data?.length === 0 ? '104px' : paperHeight ? paperHeight : '185px',
            width: '100%',
          }}
          ref={virtualListRef}
          endReached={fetchMore}
          totalCount={typeof refresh === 'function' ? (requestData?.data ?? []).length : filteredRequestData.length + 1}
          data={typeof refresh === 'function' ? requestData?.data ?? [] : filteredRequestData}
          itemContent={rowRenderer}
          overscan={300}
          increaseViewportBy={300}
          components={{
            EmptyPlaceholder: () => {
              if (disabled || requestData?.loading || requestData?.refreshing) {
                return (
                  <Box
                    css={css`
                      width: 100%;
                      display: flex;
                      justify-content: center;
                      align-items: center;
                      padding: 16px;
                    `}
                  >
                    <Loader size={24} />
                  </Box>
                );
              }
              return (
                <EmptyDataMessage
                  message={
                    requestData?.error?.message ||
                    (typeof refresh !== 'function'
                      ? optionsEmptyMessage || 'No options to display'
                      : optionsEmptyMessage || 'Something went wrong')
                  }
                  messageStyles={emptyDataMessageStyles}
                  containerStyles={emptyDataContainerStyles}
                />
              );
            },
            Footer: () => {
              if ((requestData as PaginatedQueryRequest<T, K>)?.isFetchingMore) {
                return <Loader size={24} />;
              }

              if ((requestData as PaginatedQueryRequest<T, K>)?.error) {
                return (
                  <Box
                    css={css`
                      display: flex;
                      flex-direction: column;
                      justify-content: center;
                      align-items: center;
                    `}
                  >
                    <QSTypography
                      css={css`
                        font-size: 14px;
                      `}
                    >
                      {(requestData as PaginatedQueryRequest<T, K>)?.error?.message || 'Something went wrong'}
                    </QSTypography>
                    <QSButton
                      variant="text"
                      onClick={fetchMore}
                      css={css`
                        background: none;
                      `}
                    >
                      RETRY
                    </QSButton>
                  </Box>
                );
              }

              return null;
            },
          }}
        />
        {customButtonBottom ? customButtonBottom : null}
      </Popover>
    </Box>
  );
};

export default DropdownVirtualList;
